import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { SchemaForm, cleanValues, initialValues, validate, type FieldOverride, type FormValues } from '@/components/forms/schema-form';
import { Button, type ButtonProps } from '@/components/ui/button';
import { Dialog, useConfirm } from '@/components/ui/overlay';
import { api, ApiError, fillPath } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { hasOperation, type ActionOperation } from '@/lib/openapi';
import { errorMessage } from '@/lib/query';
import { humanize } from '@/lib/utils';

const DANGEROUS = /cancel|revoke|void|refund|delete|no-show|suspend|remove|hide|decline|reject|close/;

export function actionLabel(verb: string): string {
  return humanize(verb.replace(/-/g, '_'));
}

/**
 * Runs one API operation from the contract: actions with a body open a generated form (prefilled from a
 * matching GET when the action replaces a sub-resource), actions without a body ask for confirmation.
 */
export function ActionButton({ action, params, context, onDone, overrides, children, ...buttonProps }: { action: ActionOperation; params: Record<string, string>; context?: string; onDone?(result: unknown): void; overrides?: Record<string, FieldOverride>; children?: ReactNode } & Omit<ButtonProps, 'onClick'>) {
  const { t, label } = useI18n();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<FormValues>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const path = fillPath(action.path, params);
  const name = actionLabel(action.verb);
  const destructive = DANGEROUS.test(action.verb);

  const run = async (body?: FormValues) => {
    setBusy(true);
    try {
      const result = await api[action.method === 'put' ? 'put' : action.method === 'patch' ? 'patch' : 'post'](path, body ?? {}, { idempotent: action.idempotent });
      toast.success(`${name} — done`);
      setOpen(false);
      void qc.invalidateQueries();
      onDone?.(result);
    } catch (e) {
      if (e instanceof ApiError) setErrors(e.fieldErrors());
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    if (action.schema) {
      let initial = initialValues(action.schema);
      if (action.method === 'put' && hasOperation('get', action.path)) {
        try {
          const current = await api.get<Record<string, unknown>>(path);
          initial = initialValues(action.schema, current);
        } catch {
          // nothing to prefill
        }
      }
      setValues(initial);
      setErrors({});
      setOpen(true);
      return;
    }
    if (await confirm({ title: `${name}?`, description: action.description, destructive, confirmLabel: name })) await run();
  };

  return (
    <>
      <Button variant={destructive ? 'outline' : 'secondary'} size="sm" onClick={() => void start()} loading={busy && !open} {...buttonProps} className={destructive ? 'text-destructive hover:text-destructive' : buttonProps.className}>
        {children ?? name}
      </Button>
      {action.schema && (
        <Dialog
          open={open}
          onOpenChange={setOpen}
          title={name}
          description={action.description}
          size="lg"
          footer={
            <>
              <Button variant="outline" onClick={() => setOpen(false)}>
                {t('Cancel')}
              </Button>
              <Button
                variant={destructive ? 'destructive' : 'primary'}
                loading={busy}
                onClick={() => {
                  const errs = validate(action.schema!, values, label);
                  setErrors(errs);
                  if (!Object.keys(errs).length) void run(cleanValues(action.schema!, values));
                }}
              >
                {name}
              </Button>
            </>
          }
        >
          <SchemaForm schema={action.schema} value={values} onChange={setValues} errors={errors} context={context} overrides={overrides} />
        </Dialog>
      )}
    </>
  );
}
